import React from 'react'
import { getPostureStatus } from '../SecurityPosture/utils'
import { AUGMENTT_POSTURE_STATES, MICROSOFT_POSTURE_STATES } from '../SecurityPosture/constants'

/** Fallback when intl is unavailable (e.g. PDF payload build with `intl: null`). */
const FALLBACK_MESSAGE_TEXT = {
  'UIC.GENERAL.FALSE': 'False',
  'UIC.GENERAL.NOT': 'not',
  'UIC.GENERAL.TRUE': 'True',
}

/**
 * Coerces export/table cell values to plain text. Handles strings, arrays, React elements
 * (including FormattedMessage with nested `values`), and JSON-serialized element shapes
 * from status detail formatters.
 */
export const normalizeComplianceAuditCellValue = (value, intl) => {
  const formatMessageId = (id, values) => {
    if (!id) {
      return ''
    }

    const normalizedValues =
      values && typeof values === 'object'
        ? Object.fromEntries(
            Object.entries(values).map(([key, entry]) => [key, normalizeComplianceAuditCellValue(entry, intl)]),
          )
        : undefined

    if (intl) {
      try {
        const formatted = intl.formatMessage({ id }, normalizedValues)
        // After normalizing nested FormattedMessage values to strings, formatMessage should
        // return plain text. Recurse if a rich-text node slips through.
        if (typeof formatted === 'string' || typeof formatted === 'number') {
          return String(formatted)
        }

        if (formatted == null) {
          return ''
        }

        return normalizeComplianceAuditCellValue(formatted, intl)
      } catch {
        // ignore missing message
      }
    }

    return FALLBACK_MESSAGE_TEXT[id] ?? id
  }

  if (value == null || value === '') {
    return ''
  }

  if (typeof value === 'string' || typeof value === 'number') {
    return String(value)
  }

  if (typeof value === 'boolean') {
    return String(value)
  }

  if (React.isValidElement(value)) {
    if (value.props?.id) {
      return formatMessageId(value.props.id, value.props.values)
    }

    if (value.props?.children != null) {
      return normalizeComplianceAuditCellValue(value.props.children, intl)
    }

    return ''
  }

  if (Array.isArray(value)) {
    return value.map((item) => normalizeComplianceAuditCellValue(item, intl)).join('')
  }

  if (typeof value === 'object' && value.props?.id) {
    return formatMessageId(value.props.id, value.props.values)
  }

  return ''
}

/** Maps localized status labels (from export/PDF rows) to Badge variants aligned with posture colors. */
const STATUS_LABELS_BY_VARIANT = {
  success: ['SECURE.POSTURE.CONFIGURED'],
  warning: ['SECURE.POSTURE.PARTIALLY_CONFIGURED'],
  danger: ['SECURE.POSTURE.NOT_CONFIGURED', 'SECURITY_CHECK.STATE.RISK_ACCEPTED'],
  primary: [
    'SECURE.POSTURE.RESOLVED',
    'SECURITY_CHECK.STATE.THIRD_PARTY',
    'SECURITY_CHECK.STATE.ALTERNATE_MITIGATION',
    'SECURITY_CHECK.STATE.PLANNED',
    'SECURITY_CHECK.STATE.AUGMENTT_COMPLIANCE',
  ],
  secondary: ['SECURE.POSTURE.NOT_MEASURED', 'SECURE.POSTURE.IGNORED'],
}

export const getComplianceAuditStatusBadgeVariant = (formattedStatus, intl) => {
  if (!formattedStatus) {
    return 'secondary'
  }

  const entries = Object.entries(STATUS_LABELS_BY_VARIANT)
  const match = entries.find(([, messageIds]) =>
    messageIds.some((id) => formattedStatus === intl.formatMessage({ id })),
  )

  return match?.[0] ?? 'secondary'
}

/**
 * For a partial-compliance row, the floored pass ratio (e.g. "83%") shown after the status
 * label — matching the in-app StatusBadge. Returns null for any non-partial status. Floored
 * so a near-complete ratio (e.g. 884/885) never rounds up to 100%.
 */
export const getPartialStatusDetail = (formattedStatus, row, intl) => {
  if (getComplianceAuditStatusBadgeVariant(formattedStatus, intl) !== 'warning') {
    return null
  }

  // With the Compliance column on, row expansion replaces `row.compliance` with control-ref text
  // and preserves the numeric pass counts under `complianceCounts` (see expandByComplianceControl).
  // Prefer that; fall back to `row.compliance` for the non-expanded shape.
  const { compliant, total } = row?.complianceCounts ?? row?.compliance ?? {}
  if (!total || compliant == null) {
    return null
  }

  return `${Math.floor((compliant / total) * 100)}%`
}

export const formatComplianceAuditTemplates = (formattedTemplates) => {
  if (Array.isArray(formattedTemplates)) {
    return formattedTemplates.join(', ')
  }

  return formattedTemplates ?? ''
}

// Posture status → the compliance bucket it rolls up into (counts + legend). Mirrors the
// in-app POSTURE_STATUS_TO_AGGREGATE_FIELD in
// frontend/.../Audit/Controls/buildTemplateComplianceFromPosture.js so the export and the
// interface bucket identically. (Keep in sync; a shared util is tracked follow-up.)
const STATUS_TO_BUCKET = {
  [AUGMENTT_POSTURE_STATES.COMPLIANT]: 'compliant',
  [AUGMENTT_POSTURE_STATES.PARTIALLY_COMPLIANT]: 'partial',
  [AUGMENTT_POSTURE_STATES.NOT_COMPLIANT]: 'nonCompliant',
  [AUGMENTT_POSTURE_STATES.RESOLVED]: 'resolved',
  [AUGMENTT_POSTURE_STATES.NOT_MEASURED]: 'notMeasured',
  // AP-12095 / AP-12747: Planned & Alternate Mitigation bucket as compliant, 3rd Party as
  // resolved, Risk Accepted as non-compliant.
  [MICROSOFT_POSTURE_STATES.THIRD_PARTY]: 'resolved',
  [MICROSOFT_POSTURE_STATES.ALTERNATE_MITIGATION]: 'compliant',
  [MICROSOFT_POSTURE_STATES.RISK_ACCEPTED]: 'nonCompliant',
  [MICROSOFT_POSTURE_STATES.PLANNED]: 'compliant',
}

// Statuses that earn full credit in the percentage numerator, mirroring the in-app
// SCORE_COMPLIANT_STATUSES. 3rd Party scores like Compliant even though it buckets as Resolved;
// pure Resolved does NOT earn score credit.
const SCORE_COMPLIANT_STATUSES = new Set([
  AUGMENTT_POSTURE_STATES.COMPLIANT,
  MICROSOFT_POSTURE_STATES.ALTERNATE_MITIGATION,
  MICROSOFT_POSTURE_STATES.PLANNED,
  MICROSOFT_POSTURE_STATES.THIRD_PARTY,
])

const EMPTY_SUMMARY = {
  compliant: 0,
  partial: 0,
  nonCompliant: 0,
  resolved: 0,
  notMeasured: 0,
  total: 0,
  percentage: 0,
}

/**
 * Aggregate a set of posture checks into one compliance summary. Scope-agnostic: pass the
 * exact checks being exported — a single assessment, all saved assessments, or all posture
 * checks — and the counts + percentage describe that scope. Bucketing and the half-credit
 * partial / full-credit score logic mirror the in-app `buildTemplateComplianceFromPosture`,
 * so the export header matches the interface AssessmentCard. Returns the percentage on a
 * 0–100 scale (the in-app value is a 0–1 ratio; both display the same via their formatters).
 */
export const buildComplianceSummary = (postureChecks) => {
  if (!Array.isArray(postureChecks) || postureChecks.length === 0) {
    return { ...EMPTY_SUMMARY }
  }

  // With the compliance column on, a single check is expanded into one row per control
  // reference (see expandByComplianceControl). Count each distinct check once — matching the
  // in-app score, which runs over unique checkIds — so the summary isn't inflated by duplicates.
  const seen = new Set()
  const uniqueChecks = postureChecks.filter((check) => {
    const key = check?.checkId
    if (key == null) {
      return true
    }
    if (seen.has(key)) {
      return false
    }
    seen.add(key)
    return true
  })

  const counts = { compliant: 0, partial: 0, nonCompliant: 0, resolved: 0, notMeasured: 0 }
  let scoreCompliant = 0

  uniqueChecks.forEach((check) => {
    const status = check ? getPostureStatus(check) : null
    counts[STATUS_TO_BUCKET[status] ?? 'notMeasured'] += 1
    if (status != null && SCORE_COMPLIANT_STATUSES.has(status)) {
      scoreCompliant += 1
    }
  })

  const total = uniqueChecks.length
  const percentage = Math.round(((scoreCompliant + counts.partial * 0.5) / total) * 100)

  return { ...counts, total, percentage }
}
